/**
 * High-performance market depth store.
 *
 * Stream rate from MT5 can be very high; this store is engineered to:
 *  - keep per-symbol state in a Map for O(1) symbol lookup
 *  - bump only the symbol that changed (no global recompute)
 *  - expose a non-reactive `getSnapshot` getter so chart components can read
 *    DOM rows imperatively without subscribing the React tree
 *  - keep stable bid/ask arrays, sorted and ready to render
 *
 * Subscription ownership:
 *  - There is exactly one "active" symbol at a time (the chart-selected symbol).
 *  - When the active symbol changes, the previous symbol's *cached snapshot* is
 *    intentionally preserved so the panel can show stale-but-instant data on
 *    revisit while a fresh subscription is being negotiated.
 */

import { create } from "zustand";
import {
  type BookBar,
  type MarketDepthEntry,
  type MarketDepthSnapshot,
  type MarketDepthSubscriptionStatus,
  type SymbolBook,
  VybeBookBarType,
} from "../types";

interface MarketDepthState {
  bySymbol: Map<string, MarketDepthEntry>;
  /** Currently active subscription symbol (the chart-selected one). */
  activeSymbol: string | null;
  /** Bumps whenever the structural shape of `bySymbol` changes (entry add/remove/status). */
  structureVersion: number;
}

interface MarketDepthActions {
  /** Set the active subscription target. Used by the subscription owner. */
  setActiveSymbol: (symbol: string | null) => void;
  /** Mark a symbol as subscribing (request sent, waiting for ack). Preserves any cached snapshot. */
  setSubscribing: (symbol: string) => void;
  /** Apply a server ack. Sets status `subscribed` or `unavailable`/`error` and stores depthOfMarket. */
  applySubscribeAck: (response: {
    symbol: string;
    success: boolean;
    errorMessage?: string | null;
    depthOfMarket?: number;
  }) => void;
  /** Apply server ack for unsubscribe. Marks the symbol idle but KEEPS its cached snapshot. */
  applyUnsubscribeAck: (symbol: string) => void;
  /** Mark a symbol idle locally without removing its cache. Used when stepping off a symbol. */
  markIdle: (symbol: string) => void;
  /** Locally drop a symbol entry entirely (explicit clear, e.g. account switch). */
  removeSymbol: (symbol: string) => void;
  /** Apply a streaming MT5 SymbolBook payload. Bumps only the affected entry. */
  applyMarketDepth: (book: SymbolBook) => void;
  /** Synchronous snapshot accessor (no React subscription). */
  getSnapshot: (symbol: string) => MarketDepthSnapshot | undefined;
  /** Status without subscribing. */
  getStatus: (symbol: string) => MarketDepthSubscriptionStatus;
  reset: () => void;
}

export type MarketDepthStore = MarketDepthState & MarketDepthActions;

const initialState: MarketDepthState = {
  bySymbol: new Map(),
  activeSymbol: null,
  structureVersion: 0,
};

const isBidType = (type: VybeBookBarType): boolean =>
  type === VybeBookBarType.BuyBook || type === VybeBookBarType.BuyMarket;

const isAskType = (type: VybeBookBarType): boolean =>
  type === VybeBookBarType.SellBook || type === VybeBookBarType.SellMarket;

function buildSnapshot(
  symbol: string,
  bars: BookBar[],
  previous: MarketDepthSnapshot | undefined
): MarketDepthSnapshot {
  const bids: BookBar[] = [];
  const asks: BookBar[] = [];

  for (const bar of bars) {
    if (!bar || bar.volume === 0) continue;
    if (isBidType(bar.type)) {
      bids.push(bar);
    } else if (isAskType(bar.type)) {
      asks.push(bar);
    }
  }

  bids.sort((a, b) => b.price - a.price);
  asks.sort((a, b) => a.price - b.price);

  return {
    symbol,
    bids,
    asks,
    version: (previous?.version ?? 0) + 1,
    receivedAt: Date.now(),
  };
}

export const useMarketDepthStore = create<MarketDepthStore>((set, get) => ({
  ...initialState,

  setActiveSymbol: (symbol: string | null) => {
    set((state) => {
      if (state.activeSymbol === symbol) return state;
      return { activeSymbol: symbol };
    });
  },

  setSubscribing: (symbol: string) => {
    set((state) => {
      const next = new Map(state.bySymbol);
      const existing = next.get(symbol);
      // Preserve any cached snapshot so the panel can render instantly
      // while waiting for the fresh server ack/data.
      next.set(symbol, {
        symbol,
        status: "subscribing" as MarketDepthSubscriptionStatus,
        depthOfMarket: existing?.depthOfMarket ?? 0,
        errorMessage: null,
        snapshot: existing?.snapshot,
      });
      return {
        bySymbol: next,
        structureVersion: state.structureVersion + 1,
      };
    });
  },

  applySubscribeAck: ({ symbol, success, errorMessage, depthOfMarket }) => {
    set((state) => {
      const next = new Map(state.bySymbol);
      const existing = next.get(symbol);
      let status: MarketDepthSubscriptionStatus;
      if (success) {
        status = "subscribed";
      } else if (
        typeof errorMessage === "string" &&
        errorMessage.toLowerCase().includes("does not provide")
      ) {
        status = "unavailable";
      } else {
        status = "error";
      }
      next.set(symbol, {
        symbol,
        status,
        depthOfMarket: depthOfMarket ?? existing?.depthOfMarket ?? 0,
        errorMessage: success ? null : errorMessage ?? null,
        snapshot: existing?.snapshot,
      });
      return {
        bySymbol: next,
        structureVersion: state.structureVersion + 1,
      };
    });
  },

  applyUnsubscribeAck: (symbol: string) => {
    set((state) => {
      const existing = state.bySymbol.get(symbol);
      if (!existing) return state;
      // Keep the cache, just flip status to idle so the next visit can show
      // stale-but-instant data while a fresh subscription is negotiated.
      if (existing.status === "idle") return state;
      const next = new Map(state.bySymbol);
      next.set(symbol, {
        ...existing,
        status: "idle",
        errorMessage: null,
      });
      return {
        bySymbol: next,
        structureVersion: state.structureVersion + 1,
      };
    });
  },

  markIdle: (symbol: string) => {
    set((state) => {
      const existing = state.bySymbol.get(symbol);
      if (!existing) return state;
      if (existing.status === "idle") return state;
      const next = new Map(state.bySymbol);
      next.set(symbol, {
        ...existing,
        status: "idle",
      });
      return {
        bySymbol: next,
        structureVersion: state.structureVersion + 1,
      };
    });
  },

  removeSymbol: (symbol: string) => {
    set((state) => {
      if (!state.bySymbol.has(symbol)) return state;
      const next = new Map(state.bySymbol);
      next.delete(symbol);
      return {
        bySymbol: next,
        structureVersion: state.structureVersion + 1,
      };
    });
  },

  applyMarketDepth: (book: SymbolBook) => {
    if (!book || !book.symbol) return;
    set((state) => {
      const next = new Map(state.bySymbol);
      const existing = next.get(book.symbol);
      const snapshot = buildSnapshot(book.symbol, book.bars ?? [], existing?.snapshot);
      next.set(book.symbol, {
        symbol: book.symbol,
        status: existing?.status ?? "subscribed",
        depthOfMarket: existing?.depthOfMarket ?? 0,
        errorMessage: existing?.errorMessage ?? null,
        snapshot,
      });
      // structureVersion intentionally unchanged for high-frequency stream updates
      // so consumers that only care about subscription topology won't re-render.
      return { bySymbol: next };
    });
  },

  getSnapshot: (symbol: string) => get().bySymbol.get(symbol)?.snapshot,
  getStatus: (symbol: string) =>
    get().bySymbol.get(symbol)?.status ?? "idle",

  reset: () => {
    set({ ...initialState, bySymbol: new Map() });
  },
}));
